/**
 * Reusable Data Card Component
 *
 * A generic card component with built-in features:
 * - Collapsible header with chevron icon
 * - Search functionality
 * - Pagination
 * - Scrollable content area
 * - Custom header actions
 * - Custom body content
 */

import { useState, useMemo, ReactNode } from 'react';
import { Card, Badge, Collapse, Pagination } from 'react-bootstrap';
import RefreshButton from './RefreshButton';

export interface DataCardProps<T> {
  /** Unique identifier for this card */
  cardKey: string;
  /** Card title */
  title: string;
  /** Badge count to display next to title */
  badgeCount?: number;
  /** Whether the card is expanded by default */
  defaultExpanded?: boolean;
  /** Data items to display */
  data: T[];
  /** Items per page for pagination */
  itemsPerPage?: number;
  /** Whether to show search box */
  showSearch?: boolean;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Function to filter data based on search term */
  filterFunction?: (item: T, searchTerm: string) => boolean;
  /** Custom header actions (buttons, etc.) */
  headerActions?: ReactNode;
  /** Function to render table rows */
  renderTable?: (items: T[]) => ReactNode;
  /** Custom body content (alternative to table) */
  children?: ReactNode;
  /** Callback when refresh button is clicked */
  onRefresh?: () => void;
  /** Maximum height for scrollable content area */
  maxHeight?: string;
  /** Whether to show pagination */
  showPagination?: boolean;
  /** Additional content to show before table (e.g., timeline) */
  beforeTable?: ReactNode;
  /** Empty state message */
  emptyMessage?: string;
}

export default function DataCard<T>(props: DataCardProps<T>) {
  // Destructure all props - cardKey is used by parent for React key, not needed here
  const {
    title,
    badgeCount,
    defaultExpanded = true,
    data,
    itemsPerPage = 10,
    showSearch = true,
    searchPlaceholder = 'Search...',
    filterFunction,
    headerActions,
    renderTable,
    children,
    onRefresh,
    maxHeight = '400px',
    showPagination = true,
    beforeTable,
    emptyMessage = 'No items found.',
  } = props;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim() || !filterFunction) {
      return data;
    }
    return data.filter((item) => filterFunction(item, searchTerm));
  }, [data, searchTerm, filterFunction]);

  // Paginate filtered data
  const paginatedData = useMemo(() => {
    if (!showPagination) {
      return filteredData;
    }
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage, showPagination]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    if (!showPagination) return 1;
    return Math.ceil(filteredData.length / itemsPerPage);
  }, [filteredData.length, itemsPerPage, showPagination]);

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <Card>
      <Card.Header
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="d-flex align-items-center">
          <i
            className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} me-2`}
            style={{ width: '16px' }}
          />
          <strong>{title}</strong>
          {badgeCount !== undefined && (
            <Badge bg="secondary" className="ms-2">
              {badgeCount}
            </Badge>
          )}
        </div>
        <div onClick={(e) => e.stopPropagation()} className="d-flex align-items-center gap-2">
          {onRefresh && <RefreshButton onRefresh={onRefresh} size="sm" />}
          {headerActions}
        </div>
      </Card.Header>
      <Collapse in={isExpanded}>
        <div>
          <Card.Body style={{ maxHeight, overflowY: 'auto' }}>
            {/* Search Box */}
            {showSearch && (
              <div className="mb-3">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => handleSearchChange('')}
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Before Table Content (e.g., timeline) */}
            {beforeTable}

            {/* Custom Children or Table */}
            {children ? (
              children
            ) : renderTable ? (
              <>
                {filteredData.length === 0 ? (
                  <div className="text-center py-4 text-muted">{emptyMessage}</div>
                ) : (
                  <>
                    {renderTable(paginatedData)}
                    {/* Pagination */}
                    {showPagination && totalPages > 1 && (
                      <div className="mt-3 d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                          {Math.min(currentPage * itemsPerPage, filteredData.length)} of{' '}
                          {filteredData.length} items
                        </small>
                        <Pagination className="mb-0">
                          <Pagination.First
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                          />
                          <Pagination.Prev
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          />
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            return (
                              <Pagination.Item
                                key={pageNum}
                                active={pageNum === currentPage}
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </Pagination.Item>
                            );
                          })}
                          <Pagination.Next
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                          />
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="text-center py-4 text-muted">{emptyMessage}</div>
            )}
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
}
